import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Media } from '@/js/libs/ui';
import { Icons, Input } from '@/js/libs/ui/src/V2';

interface DefaultSearchFilterProps {
  disabled?: boolean;
  intOnly?: boolean;
  filter?: string;
  placeholder?: string;
  saveFilter?: (value: string) => void;
  fullWidth?: boolean;
  dataTestId?: string;
}

interface SearchFilterWithDispatchProps extends DefaultSearchFilterProps {
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  handleSetFilter?: never;
}

interface SearchFilterWithHandleDispatchProps extends DefaultSearchFilterProps {
  setFilter?: never;
  handleSetFilter: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

type SearchFilterProps = SearchFilterWithDispatchProps | SearchFilterWithHandleDispatchProps;

export const SearchFilter: React.FC<SearchFilterProps> = React.memo(function SearchFilter({
  disabled,
  intOnly,
  filter,
  setFilter,
  handleSetFilter,
  placeholder,
  saveFilter,
  fullWidth,
  dataTestId = 'search-field',
}) {
  const { colors } = useTheme();
  const isMobile = useMediaQuery({ query: Media.xsMax });
  const [t] = useTranslation(['backend', 'common']);

  const isChangeAllowed = useCallbackRef((e: React.ChangeEvent<HTMLInputElement>): boolean => {
    const { value } = e.currentTarget;
    if (intOnly && value.length > 0) {
      return /^\d+$/.test(value);
    }
    return true;
  });

  const onFilterChangeChange = useCallbackRef((e: React.ChangeEvent<HTMLInputElement>) => {
    if (isChangeAllowed(e)) {
      saveFilter?.(e.currentTarget.value);
      return setFilter?.(e.currentTarget.value) ?? handleSetFilter?.(e);
    }
    return null;
  });

  const onClear = useCallbackRef((e: React.MouseEvent<Element, MouseEvent>) => {
    setFilter?.('');
    saveFilter?.('');
    handleSetFilter?.({
      ...e,
      target: {
        ...e.target,
        value: '',
      },
      currentTarget: {
        ...e.currentTarget,
        value: '',
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  });

  return (
    <Input
      disabled={disabled}
      width={fullWidth || isMobile ? '100%' : '220px'}
      placeholder={placeholder ?? t('common:PLACEHOLDER_SEARCH')}
      value={filter}
      onChange={onFilterChangeChange}
      icon={
        filter ? (
          <Icons.Clear filled size={15} color={colors.grey100_1} onClick={onClear} data-testid='icons-clear' />
        ) : (
          <Icons.Search size={15} color={colors.grey100_1} data-testid='icons-search' />
        )
      }
      isActionIcon={!!filter}
      dataTestId={dataTestId}
    />
  );
});
